/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import {
  createContext,
  useReducer,
  useState,
  useMemo,
  useEffect,
  type Reducer,
  type ComponentType,
  type PropsWithChildren,
  type Dispatch,
} from 'react';
import styled, { useTheme } from 'styled-components';
import { isObject } from 'lodash-es';
import { Modal as DefModal, useMediaQuery } from '@mui/material';
import { useWindowSize } from '../hooks';
import { getDownMedia, maxContentWidth } from '../styles';
import { AlertModalWrapper } from '../styled';
import {
  RebalanceOrSellError,
  RebalanceOrSellExpired,
  RebalanceOrBuyOrSellReused,
  PendingExecRebalance,
  PendingSellCrypto,
  PaymentSubscription,
  PaymentSingle,
  InitialPortfolioRebalanceStart,
  InitialPortfolioRebalanceError,
  UpdateExchangeKeysSuccess,
  SubscriptionDisabledSuccess,
  ChangeEmailSuccess,
  ChangeEmailError,
  ChangeEmailExpired,
  LockedCurrency,
  UnlockedCurrency,
  AumBelowMinimum,
  AccountRemovementEmailConfirm,
  RemoveAccountLinkExpired,
  RebalanceInProgress,
  RebalanceFinalised,
  RebalanceFailed,
  BuySellOrderInProgress,
  BuySellOrderFinalised,
  BuySellOrderFailed,
  BitvavoExchangeLocked,
  KrakenExchangeLocked,
  SellLeftMoneyError,
  DepositError,
  DepositLeftMoneyError,
  DepositExpired,
} from '../components/alert-modals';
import { ModalCloseButton } from '../components/modal-elements';

const AlertModals = ({ children }: PropsWithChildren) => {
  const { drawer } = useTheme();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [contentRightMargin, setContentRightMargin] = useState(0);

  const { modalOpened, Content, data } = state;

  const closeModal = () => dispatch(['closeModal']);

  const contextValue = useMemo(() => ({ ...state, dispatch }), [state]);

  const maxScreenWidth = useMemo(
    () => (drawer?.currentWidth ?? 0) + maxContentWidth,
    [drawer?.currentWidth]
  );

  const { width } = useWindowSize();

  const moreThanMaxWidth = useMediaQuery(`(min-width:${maxScreenWidth}px)`);

  useEffect(() => {
    if (moreThanMaxWidth) {
      setContentRightMargin(window.innerWidth - maxScreenWidth);
    } else {
      setContentRightMargin(0);
    }
  }, [width, moreThanMaxWidth, maxScreenWidth]);

  return (
    <AlertModalContext.Provider value={contextValue}>
      {children}
      <Modal open={modalOpened} disableEnforceFocus onClose={closeModal}>
        <ContentWrapper elevation={24} $rightMargin={contentRightMargin}>
          <ModalCloseButton onClick={closeModal} />
          {/*
          //@ts-ignore*/}
          <Content {...(isObject(data) ? data : {})} />
        </ContentWrapper>
      </Modal>
    </AlertModalContext.Provider>
  );
};

type State = {
  openedModalName: Nullish<AlertModalContentType>;
  modalOpened: boolean;
  Content: ComponentType;
  data: unknown;
  queue: Array<{
    modalType: AlertModalContentType;
    data?: unknown;
  }>;
};

const initialState: State = {
  openedModalName: null,
  modalOpened: false,
  Content: () => null,
  data: undefined,
  queue: [],
};

const modalsData = {
  rebalanceOrSellError: RebalanceOrSellError,
  rebalanceOrSellExpired: RebalanceOrSellExpired,
  rebalanceOrBuyOrSellReused: RebalanceOrBuyOrSellReused,
  pendingExecRebalance: PendingExecRebalance,
  pendingSellCrypto: PendingSellCrypto,
  paymentSubscription: PaymentSubscription,
  paymentSingle: PaymentSingle,
  initialPortfolioRebalanceStart: InitialPortfolioRebalanceStart,
  initialPortfolioRebalanceError: InitialPortfolioRebalanceError,
  updateExchangeKeysSuccess: UpdateExchangeKeysSuccess,
  subscriptionDisabledSuccess: SubscriptionDisabledSuccess,
  changeEmailSuccess: ChangeEmailSuccess,
  changeEmailError: ChangeEmailError,
  changeEmailExpired: ChangeEmailExpired,
  lockedCurrency: LockedCurrency,
  unlockedCurrency: UnlockedCurrency,
  aumBelowMinimum: AumBelowMinimum,
  accountRemovementEmailConfirm: AccountRemovementEmailConfirm,
  removeAccountLinkExpired: RemoveAccountLinkExpired,
  rebalanceInProgress: RebalanceInProgress,
  rebalanceFinalised: RebalanceFinalised,
  rebalanceFailed: RebalanceFailed,
  buySellOrderInProgress: BuySellOrderInProgress,
  buySellOrderFinalised: BuySellOrderFinalised,
  buySellOrderFailed: BuySellOrderFailed,
  krakenExchangeLocked: KrakenExchangeLocked,
  bitvavoExchangeLocked: BitvavoExchangeLocked,
  sellLeftMoneyError: SellLeftMoneyError,
  depositError: DepositError,
  depositExpired: DepositExpired,
  depositLeftMoneyError: DepositLeftMoneyError,
} as const;

export type AlertModalContentType = keyof typeof modalsData;

type SetModalContentData = unknown;
type SetModalContentReplaceCurrent = boolean;

type Action =
  | ['closeModal']
  | [
      'setModalContent',
      AlertModalContentType,
      SetModalContentData?,
      SetModalContentReplaceCurrent?,
    ];

const reducer: Reducer<State, Action> = (
  prevState,
  [type, modalType, data, replace = false]
) => {
  switch (type) {
    case 'closeModal':
      // Check if there are modals in the queue
      if (prevState.queue.length > 0) {
        // Get the next modal from the queue
        const [nextModal, ...remainingQueue] = prevState.queue;
        const Content = modalsData[nextModal.modalType];

        return {
          openedModalName: nextModal.modalType,
          modalOpened: true,
          Content,
          data: nextModal.data,
          queue: remainingQueue,
        };
      }
      return initialState;
    case 'setModalContent': {
      const Content = !!modalType && modalsData[modalType];

      if (!Content) {
        return initialState;
      }

      // If a modal is already open, add the new modal to the queue
      if (!replace && prevState.modalOpened) {
        return {
          ...prevState,
          queue: [
            ...prevState.queue,
            {
              modalType,
              data,
            },
          ],
        };
      }

      // Otherwise, open the modal immediately (preserving the queue)
      return {
        ...prevState,
        openedModalName: modalType,
        modalOpened: true,
        Content,
        data,
      };
    }
    default:
      return prevState;
  }
};

// noinspection CssUnusedSymbol
const Modal = styled(DefModal)`
  display: flex;
  justify-content: center;
  align-items: center;

  .MuiPaper-rounded {
    border-radius: 12px;
  }
`;

const ContentWrapper = styled(AlertModalWrapper)<{
  $rightMargin: number;
}>`
  position: relative;
  outline: none;

  ${getDownMedia('md')} {
    margin-left: ${({ theme: { drawer } }) => drawer?.currentWidth ?? 0}px;
    margin-right: ${({ $rightMargin }) => $rightMargin}px;
  }
`;

export const AlertModalContext = createContext<
  State & {
    dispatch: Dispatch<Action>;
  }
>({
  ...initialState,
  dispatch: () => undefined,
});

export { AlertModals };
